import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { SiteShell } from "@/components/site/SiteShell";
import {
  confirmOrderPayment,
  type PublicOrderSummary,
} from "@/lib/checkout.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { buildHead } from "@/lib/seo";

type Search = { order?: string; session_id?: string };

export const Route = createFileRoute("/order-confirmed")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    order: typeof s.order === "string" ? s.order : undefined,
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  head: () =>
    buildHead({
      locale: "en",
      title: "Order confirmed · ShugertGEO",
      description:
        "Your GEO Readiness Audit is queued. We'll email your report when it's ready.",
      path: "/order-confirmed",
      altPath: "/order-confirmed",
      noindex: true,
    }),
  component: OrderConfirmedPage,
});

const GOLD = "#C8A84B";

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function OrderConfirmedPage() {
  const { order, session_id } = Route.useSearch();
  const confirm = useServerFn(confirmOrderPayment);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ok"; order: PublicOrderSummary }
    | { status: "error"; message: string }
  >({ status: "loading" });

  useEffect(() => {
    if (!order) {
      setState({ status: "error", message: "Missing order reference." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await confirm({
          data: {
            order_number: order,
            session_id: session_id,
            environment: session_id ? getStripeEnvironment() : undefined,
          },
        });
        if (cancelled) return;
        if ("error" in res) {
          setState({ status: "error", message: res.error });
        } else {
          setState({ status: "ok", order: res.order });
        }
      } catch (e) {
        if (!cancelled) {
          setState({
            status: "error",
            message: e instanceof Error ? e.message : "Could not load order",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order, session_id, confirm]);

  return (
    <SiteShell>
      <section className="bg-background">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
          {state.status === "loading" && (
            <p className="text-sm text-muted-foreground">Confirming your order…</p>
          )}

          {state.status === "error" && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-8">
              <h1 className="font-display text-2xl font-semibold text-foreground">
                We couldn't load that order
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">{state.message}</p>
              <p className="mt-3 text-sm text-muted-foreground">
                If your payment went through, email us at{" "}
                <a className="underline" href="mailto:hello@shugert.com.mx">
                  hello@shugert.com.mx
                </a>{" "}
                with your order number and we'll sort it out.
              </p>
            </div>
          )}

          {state.status === "ok" && (
            <div className="grid gap-8">
              <div>
                <p
                  className="text-[10px] font-medium uppercase tracking-[0.22em]"
                  style={{ color: GOLD }}
                >
                  Payment received
                </p>
                <h1 className="mt-2 font-display text-3xl font-semibold text-foreground sm:text-4xl">
                  {state.order.full_name
                    ? `Thanks, ${state.order.full_name.split(" ")[0]}, we're on it.`
                    : "Thanks, we're on it."}
                </h1>
                {state.order.plan_id === "teardown" ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Your Performance Teardown is in queue. We'll deliver the full
                    report{" "}
                    {state.order.email ? (
                      <>
                        to{" "}
                        <span className="text-foreground">{state.order.email}</span>{" "}
                      </>
                    ) : null}
                    within <span className="text-foreground">2 business days</span>.
                    No further action needed on your end.
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">
                    {state.order.email ? (
                      <>
                        We'll email a copy of your receipt and the audit report to{" "}
                        <span className="text-foreground">{state.order.email}</span>.
                      </>
                    ) : (
                      <>We'll email a copy of your receipt and the audit report shortly.</>
                    )}
                    {state.order.delivery_label && (
                      <>
                        {" "}Delivery:{" "}
                        <span className="text-foreground">{state.order.delivery_label}</span>.
                      </>
                    )}
                  </p>
                )}
              </div>

              <dl className="grid gap-3 rounded-2xl border border-border bg-card/40 p-6 text-sm">
                <Row label="Order number" value={state.order.order_number} mono />
                <Row
                  label="Plan"
                  value={
                    state.order.plan_id === "teardown"
                      ? `Shugert, ${state.order.plan_name}`
                      : `ShugertGEO, ${state.order.plan_name}`
                  }
                />
                {state.order.website_url && (
                  <Row label="Website" value={state.order.website_url} />
                )}
                <Row
                  label="Amount"
                  value={formatPrice(state.order.amount_cents, state.order.currency)}
                />
                <Row
                  label="Status"
                  value={state.order.status.replace(/_/g, " ")}
                  uppercase
                />
              </dl>

              <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  Track your order anytime in the client portal, we'll email you a magic sign-in link.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/portal"
                    className="self-start rounded-full px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
                    style={{ backgroundColor: GOLD }}
                  >
                    Open client portal →
                  </Link>
                  <Link
                    to="/geo-readiness"
                    className="self-start rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground hover:bg-card"
                  >
                    ← Back to ShugertGEO
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}

function Row({
  label,
  value,
  mono,
  uppercase,
}: {
  label: string;
  value: string;
  mono?: boolean;
  uppercase?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border pb-3 last:border-0 last:pb-0">
      <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={[
          "text-foreground",
          mono ? "font-mono text-sm" : "text-sm",
          uppercase ? "uppercase tracking-[0.12em]" : "",
        ].join(" ")}
      >
        {value}
      </dd>
    </div>
  );
}